import { FieldButton, FieldSelect } from '@dixa/lib-shared-habla';
import * as React from 'react';
import { connect } from 'react-redux';
import {
  Field,
  formValueSelector,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';
import { email, numericality, required } from 'redux-form-validators';

import { durationConverter } from 'utils/duration';

import { EmailPanelForms } from '../../../EmailPanel/emailPanelForms';

import { DurationInput } from './components/durationInput';
import style from './index.module.scss';

interface OwnProps {
  existingEmails?: string[];
  isLoading?: boolean;
  disabled?: boolean;
}

interface StateProps {
  senderEmailValue?: string | null;
}

enum Fields {
  senderEmail = 'senderEmail',
  replyToEmail = 'replyToEmail',
  delay = 'delay',
}

export interface FormValues {
  [Fields.senderEmail]?: string | null;
  [Fields.replyToEmail]?: string | null;
  [Fields.delay]?: string;
}

type Props = OwnProps & StateProps & InjectedFormProps<FormValues, OwnProps>;

const CSAT_MIN_DELAY_IN_SECONDS = 60;
const csatMinDelayInMinutes = durationConverter.seconds.toMinutes(
  CSAT_MIN_DELAY_IN_SECONDS,
);
const delayInputMinValueMinutes = Number.isInteger(csatMinDelayInMinutes)
  ? csatMinDelayInMinutes.toString()
  : csatMinDelayInMinutes.toFixed(1);

const TWO_WEEKS_IN_HOURS = 14 * 24;
const CSAT_MAX_DELAY_IN_SECONDS =
  durationConverter.hours.toSeconds(TWO_WEEKS_IN_HOURS);
const csatMaxDelayInMinutes = durationConverter.seconds.toHoursAndMinutes(
  CSAT_MAX_DELAY_IN_SECONDS,
);

const delayInputValidationMessages = {
  tooSmall: `must be at least ${delayInputMinValueMinutes} minute${
    csatMinDelayInMinutes > 1 ? 's' : ''
  }`,
  tooBig: `Can't be above ${csatMaxDelayInMinutes.hours} hour(s) ${
    csatMaxDelayInMinutes.minutes > 1
      ? `and ${Math.floor(csatMaxDelayInMinutes.minutes)} minute(s)`
      : ''
  }`,
};

const EmailCSATSettingsFormView: React.FunctionComponent<Props> = (props) => {
  const emailAccountsSuggestions =
    props.existingEmails?.map((email) => ({ id: email, name: email })) ?? [];

  return (
    <form className={style.root} onSubmit={props.handleSubmit}>
      <Field
        type="email"
        disabled={props.disabled}
        name={Fields.senderEmail}
        placeholder="Leave blank to use the email address the user wrote to"
        validate={[email({ allowBlank: true })]}
        label='Override CSAT "sender" email address'
        component={FieldSelect}
        helpIcon={{
          text: 'The chosen email is the one your users will receive a CSAT Survey from. If you wish to add a new email as the sender email, this can be added in the top of Settings -> Channels and flows -> Email. Leaving this blank will fallback to sending it from the email your users wrote to.',
        }}
        suggestions={emailAccountsSuggestions}
        searchable
        clearable
        items={emailAccountsSuggestions}
        searchSuggestion={(items, search) =>
          items.find((item) => item.name.toLowerCase() === search.toLowerCase())
        }
      />

      <Field
        type="email"
        disabled={props.disabled}
        component={FieldSelect}
        name={Fields.replyToEmail}
        label='Override "reply to" email address'
        validate={[email({ allowBlank: true })]}
        helpIcon={{
          text: 'Leave blank to use sender address',
        }}
        placeholder={
          props.senderEmailValue || 'Leave blank to use sender address'
        }
        searchable
        clearable
        items={emailAccountsSuggestions}
        searchSuggestion={(items, search) =>
          items.find((item) => item.name.toLowerCase() === search.toLowerCase())
        }
      />

      <FieldButton
        type="submit"
        disabled={props.disabled}
        isLoading={props.submitting || props.isLoading}
      >
        Save settings
      </FieldButton>

      <Field
        disabled={props.disabled}
        component={DurationInput}
        name={Fields.delay}
        className={style.delayField}
        hoursInputProps={{ label: 'CSAT Delay', suffix: 'Hours' }}
        minutesInputProps={{ suffix: 'Minutes' }}
        validate={[
          required(),
          numericality({
            int: true,
            '>=': CSAT_MIN_DELAY_IN_SECONDS,
            msg: {
              id: '>=',
              defaultMessage: delayInputValidationMessages.tooSmall,
            },
          }),
          numericality({
            int: true,
            '<=': CSAT_MAX_DELAY_IN_SECONDS,
            msg: {
              id: '<=',
              defaultMessage: delayInputValidationMessages.tooBig,
            },
          }),
        ]}
      />

      {props.error && (
        <div className={style.formError}>{props.error.toString()}</div>
      )}
    </form>
  );
};

const EmailCSATSettingsForm = reduxForm<FormValues, OwnProps>({
  form: EmailPanelForms.GlobalSettingsForm,
})(EmailCSATSettingsFormView);

const EmailCSATSettingsFormConnected = connect<StateProps, {}, OwnProps>(
  (state) => ({
    senderEmailValue: formValueSelector(EmailPanelForms.GlobalSettingsForm)(
      state,
      Fields.senderEmail,
    ),
  }),
)(EmailCSATSettingsForm);

export { EmailCSATSettingsFormConnected as EmailCSATSettingsForm };
